import { useState } from "react";
import { JobFinancialOverview } from "./JobFinancialOverview";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { Link as RouterLink } from "react-router-dom";
import { apiClient } from "../../lib/api";
import { usePermission } from "../../hooks/usePermission";
import { ResponsiveTable } from "../common/ResponsiveTable";
import { costCategories, remainingMode } from "../../types/profitDesk";
import type {
  CashForecast,
  CashPlan,
  CategoryAmounts,
  JobCostPlan,
  JobProfit,
  Plan,
  ProfitReport,
} from "../../types/profitDesk";

const usd = (v: string | null) =>
  v === null
    ? "Not set"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });
const today = () => new Date().toLocaleDateString("en-CA");
const emptyAmounts = () =>
  Object.fromEntries(costCategories.map((k) => [k, "0"])) as CategoryAmounts;
const errorText = (error: unknown): string => {
  const details =
    error && typeof error === "object" && "responseData" in error
      ? error.responseData
      : null;
  const flatten = (value: unknown): string =>
    typeof value === "string"
      ? value
      : Array.isArray(value)
        ? value.map(flatten).join(" ")
        : value && typeof value === "object"
          ? Object.entries(value)
              .map(([key, v]) => `${key.replaceAll("_", " ")}: ${flatten(v)}`)
              .join(" ")
          : "";
  return (
    flatten(details) ||
    (error instanceof Error ? error.message : "Could not save the plan.")
  );
};

function Field({
  label,
  value,
  onChange,
  type = "number",
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        slotProps={{
          input: {
            "aria-label": label,
            ...(type === "number" ? { step: "0.01" } : {}),
          },
        }}
      />
    </FormControl>
  );
}
function Warnings({ warnings }: { warnings: string[] }) {
  return (
    <Stack spacing={1}>
      {warnings.map((message) => (
        <Alert key={message} color="warning">
          {message}
        </Alert>
      ))}
    </Stack>
  );
}
function History({ plan }: { plan: Plan<JobCostPlan> | Plan<CashPlan> }) {
  return (
    <Box component="details">
      <Typography component="summary" level="title-sm">
        Recent approvals ({plan.history.length})
      </Typography>
      {plan.history.map((r) => (
        <Card key={r.revision} variant="plain">
          <Typography level="title-sm">
            Revision {r.revision} · {r.actor} ·{" "}
            {new Date(r.at).toLocaleString()}
          </Typography>
          <Typography>{r.data.note}</Typography>
          {"budget" in r.data ? (
            <>
              <Typography level="body-sm">
                Margin floor {r.data.margin_floor}% · Overhead allowance{" "}
                {usd(r.data.overhead)}
              </Typography>
              {costCategories.map((category) => (
                <Typography key={category} level="body-sm">
                  {category}: budget{" "}
                  {usd((r.data as JobCostPlan).budget[category])}; additional
                  remaining {(r.data as JobCostPlan).remaining_modes
                    ? remainingMode(r.data as JobCostPlan, category) === "budget"
                      ? "automatic from budget"
                      : usd((r.data as JobCostPlan).remaining[category])
                    : `${usd((r.data as JobCostPlan).remaining[category])} (as entered)`}
                </Typography>
              ))}
              <Typography level="body-sm">
                {r.data.allocations.length} commitment matches ·{" "}
                {r.data.complete_categories.join(", ")} coverage reviewed
              </Typography>
            </>
          ) : (
            <>
              <Typography level="body-sm">
                Opening cash {usd(r.data.opening_cash)} on {r.data.as_of} ·{" "}
                {r.data.weeks} weeks · {r.data.delay_days}-day delay
              </Typography>
              {r.data.expenses.map((expense, i) => (
                <Typography key={i} level="body-sm">
                  {expense.label}: {usd(expense.amount)}, starting{" "}
                  {expense.date}
                  {expense.every_weeks
                    ? `, every ${expense.every_weeks} weeks, ${expense.occurrences} occurrences`
                    : ""}
                </Typography>
              ))}
              <Typography level="body-sm">
                {r.data.collections.length} reviewed expected collection dates
              </Typography>
            </>
          )}
          {r.data.reconciliations.map((link, i) => (
            <Typography key={i} level="body-sm">
              Duplicate reconciliation: {link.reason}
            </Typography>
          ))}
        </Card>
      ))}
    </Box>
  );
}

export function JobProfitDesk({ jobId }: { jobId: string | number }) {
  const { can } = usePermission();
  const [editing, setEditing] = useState(false);
  const query = useQuery({
    queryKey: ["job-profit", String(jobId)],
    enabled: can("billing"),
    queryFn: async () =>
      (await apiClient.get<JobProfit>(`/api/jobs/${jobId}/profit-plan/`)).data,
  });
  if (!can("billing")) return null;
  if (query.error && !query.data)
    return (
      <Alert
        color="danger"
        endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}
      >
        Financial overview unavailable. Retry to load job profit and customer payments.
      </Alert>
    );
  if (!query.data) return <Typography>Loading job profit…</Typography>;
  const data = query.data;
  return (
    <Card sx={{ mb: 2 }} data-testid="job-profit-desk">
      {query.error && <Alert color="warning" endDecorator={<Button loading={query.isFetching} onClick={() => query.refetch()}>Retry</Button>}>
        Financial overview could not be refreshed. Showing previously loaded figures; they may be out of date.
      </Alert>}
      <JobFinancialOverview
        data={data}
        editing={editing}
        canEdit={can("billing", "edit")}
        onEdit={() => setEditing(!editing)}
      />
      {editing && (
        <JobPlanEditor data={data} onSaved={() => setEditing(false)} onClose={() => setEditing(false)} />
      )}
      <Divider sx={{ my: 1 }} />
      <Box component="details">
        <Typography
          component="summary"
          level="title-md"
          sx={{ cursor: "pointer", py: 1, display: "list-item", listStylePosition: "inside", "&:focus-visible": { outline: "2px solid", outlineOffset: 3 } }}
        >
          Cost details & budget
        </Typography>
        <Typography level="body-sm" sx={{ mb: 2 }}>
          See which expenses are included and how the job compares with its
          approved budget.
        </Typography>
        <Stack
          gap={1}
          sx={{ p: 2, mb: 2, bgcolor: "background.level1", borderRadius: "md" }}
        >
          {(
            [
              ["Recorded costs (credits already deducted)", data.actual_costs],
              ["Orders not yet entered as costs", data.committed_costs],
              ["Remaining costs (budget or manual estimate)", data.additional_remaining],
              ["Expected total job costs", data.projected_costs],
              ["Approved cost budget", data.approved_budget],
              [
                data.budget_variance !== null &&
                Number(data.budget_variance) < 0
                  ? "Under budget"
                  : "Over budget",
                data.budget_variance === null
                  ? null
                  : String(Math.abs(Number(data.budget_variance))),
              ],
            ] as [string, string | null][]
          ).map(([label, value]) => (
            <Stack
              direction="row"
              justifyContent="space-between"
              gap={2}
              key={label}
            >
              <Typography level="body-sm">{label}</Typography>
              <Typography level="title-sm">
                {value === null ? "Not set" : usd(value)}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {data.forecast_by_category && <>
          <Typography level="title-sm">Remaining forecast by category</Typography>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            Automatic remaining = budget − recorded spending − unmatched commitments,
            with a minimum of zero. Credits reduce total costs separately.
            Material orders use materials; subcontractor work orders use subcontractor.
          </Typography>
          <ResponsiveTable
            rows={costCategories.map((category) => ({ category, ...data.forecast_by_category![category] }))}
            getRowKey={(row) => row.category}
            columns={[
              { key: "category", header: "Category", render: (row) => row.category },
              { key: "budget", header: "Budget", render: (row) => usd(row.budget) },
              { key: "budget_used", header: "Recorded spending", render: (row) => usd(row.budget_used) },
              { key: "committed", header: "Committed", render: (row) => usd(row.committed) },
              { key: "remaining", header: "Still expected", render: (row) => usd(row.remaining) },
              { key: "mode", header: "Forecast", render: (row) => row.mode === "budget" ? "Automatic from budget" : "Manual estimate" },
            ]}
          />
        </>}
        <Typography level="title-sm" sx={{ mb: 1 }}>
          Recorded expenses & credits
        </Typography>
        <ResponsiveTable
          rows={data.sources}
          getRowKey={(r) => r.ref}
          pagination={{ pageSize: 15 }}
          columns={[
            {
              key: "label",
              header: "Source",
              mobileVariant: "header",
              render: (r) => (
                <Link component={RouterLink} to={r.url}>
                  {r.label}
                </Link>
              ),
            },
            { key: "category", header: "Category", render: (r) => r.category },
            { key: "date", header: "Recorded date", render: (r) => r.date },
            { key: "amount", header: "Amount", render: (r) => usd(r.amount) },
            {
              key: "state",
              header: "Included in actual costs",
              mobileLabel: "Counted cost",
              render: (r) =>
                r.excluded ? "Excluded: reviewed duplicate" : "Yes",
            },
          ]}
          emptyState="No recorded costs. Review cost coverage before relying on the margin."
        />
      </Box>
      <Box component="details">
        <Typography
          component="summary"
          level="title-md"
          sx={{ cursor: "pointer", py: 1, display: "list-item", listStylePosition: "inside", "&:focus-visible": { outline: "2px solid", outlineOffset: 3 } }}
        >
          Material orders & crew costs ({data.orders.length})
        </Typography>
        <Typography level="body-sm" sx={{ mb: 2 }}>
          Orders already committed to the job. Amounts not matched to recorded
          costs are included in the cost estimate above.
        </Typography>
        <ResponsiveTable
          rows={data.orders}
          getRowKey={(r) => r.ref}
          columns={[
            {
              key: "label",
              header: "Order",
              mobileVariant: "header",
              render: (r) => (
                <Link component={RouterLink} to={r.url}>
                  {r.label}
                </Link>
              ),
            },
            { key: "status", header: "Status", render: (r) => r.status },
            {
              key: "amount",
              header: "Order total",
              render: (r) => usd(r.amount),
            },
            {
              key: "remaining",
              header: "Not yet matched to costs",
              mobileLabel: "Unmatched",
              render: (r) => usd(r.remaining),
            },
          ]}
        />
      </Box>
      {data.plan.history.length > 0 && <History plan={data.plan} />}
    </Card>
  );
}

function JobPlanEditor({
  data,
  onSaved,
  onClose,
}: {
  data: JobProfit;
  onSaved: () => void;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [expectedRevision] = useState(data.plan.revision);
  const [draft, setDraft] = useState<JobCostPlan>({
    budget: emptyAmounts(),
    remaining: emptyAmounts(),
    margin_floor: "30",
    overhead: "0",
    reconciliations: [],
    allocations: [],
    complete_categories: [],
    ...data.plan.data,
    remaining_modes: Object.fromEntries(costCategories.map((category) => [category, remainingMode(data.plan.data, category)])) as JobCostPlan["remaining_modes"],
    note: "",
  });
  const [duplicate, setDuplicate] = useState("");
  const [canonical, setCanonical] = useState("");
  const [reason, setReason] = useState("");
  const [order, setOrder] = useState("");
  const [source, setSource] = useState("");
  const [allocationAmount, setAllocationAmount] = useState("");
  const save = useMutation({
    mutationFn: async () =>
      (
        await apiClient.patch<JobProfit>(
          `/api/jobs/${data.job_id}/profit-plan/`,
          { ...draft, expected_revision: expectedRevision },
        )
      ).data,
    onSuccess: async (result) => {
      await client.invalidateQueries({
        queryKey: ["jobFinancialSummary", String(data.job_id)],
      });
      await client.invalidateQueries({ queryKey: ["cash-desk"] });
      client.setQueryData(["job-profit", String(data.job_id)], result);
      await client.invalidateQueries({
        queryKey: ["job-workspace", String(data.job_id)],
      });
      await client.invalidateQueries({ queryKey: ["profit-desk"] });
      onSaved();
    },
  });
  return (
    <Modal open onClose={() => !save.isPending && onClose()}>
      <ModalDialog
        aria-labelledby="job-cost-plan-title"
        sx={{ width: 'min(1100px, calc(100vw - 24px))', maxHeight: 'calc(100dvh - 32px)', p: { xs: 2, sm: 3 }, overflow: 'hidden' }}
      >
        <ModalClose disabled={save.isPending} />
        <Typography id="job-cost-plan-title" level="h4" component="h2" sx={{ pr: 4 }}>Review cost plan</Typography>
        <Typography level="body-sm">{data.job_number} · {data.name}</Typography>
    <Box
      component="form"
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, gap: 2 }}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Stack spacing={2} sx={{ overflowY: 'auto', minHeight: 0, pr: 1, pb: 1 }}>
      <Typography level="title-lg">
        Approve budget and remaining costs
      </Typography>
      <Typography level="body-sm">
        Budgets are immutable after approval; this creates a new revision.
        Use budget automatically to estimate unspent costs as records and commitments
        arrive. Or set remaining costs manually, excluding costs already recorded or
        committed. Enter zero in manual mode when no more costs are expected.
        Coverage reviewed is a review acknowledgement, not a completion switch.
      </Typography>
      {costCategories.map((category) => (
        <Box
          key={category}
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: '1fr 1.5fr 1.2fr 1.2fr' }, gap: 1.5, alignItems: 'end', p: 1.5, borderRadius: 'sm', bgcolor: 'background.level1', '& > *': { minWidth: 0 } }}
        >
          <Field
            label={`${category} budget`}
            value={draft.budget[category]}
            onChange={(v) =>
              setDraft({ ...draft, budget: { ...draft.budget, [category]: v } })
            }
          />
          <FormControl>
            <FormLabel>{category} forecast</FormLabel>
            <Select
              aria-label={`${category} forecast`}
              value={remainingMode(draft, category)}
              onChange={(_, value) => value && setDraft({
                ...draft,
                remaining_modes: { ...draft.remaining_modes!, [category]: value },
              })}
            >
              <Option value="budget">Use budget automatically</Option>
              <Option value="manual">Set remaining costs manually</Option>
            </Select>
          </FormControl>
          {remainingMode(draft, category) === "manual" ? (
            <Field
              label={`${category} remaining costs`}
              value={draft.remaining[category]}
              onChange={(v) => setDraft({ ...draft, remaining: { ...draft.remaining, [category]: v } })}
            />
          ) : (
            <Typography level="body-sm" sx={{ pb: 1 }}>
              {usd(String(Math.max(0, Number(draft.budget[category]) - Number(data.forecast_by_category?.[category].budget_used ?? 0) - Number(data.forecast_by_category?.[category].committed ?? 0))))} remaining from budget
            </Typography>
          )}
          <Checkbox
            label={`${category} coverage reviewed`}
            checked={draft.complete_categories.includes(category)}
            onChange={(e) =>
              setDraft({
                ...draft,
                complete_categories: e.target.checked
                  ? [...draft.complete_categories, category]
                  : draft.complete_categories.filter((c) => c !== category),
              })
            }
          />
        </Box>
      ))}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Field
          label="Margin floor (%)"
          value={draft.margin_floor}
          onChange={(v) => setDraft({ ...draft, margin_floor: v })}
        />
        <Field
          label="Overhead allowance ($)"
          value={draft.overhead}
          onChange={(v) => setDraft({ ...draft, overhead: v })}
        />
      </Stack>
      <Divider />
      <Typography level="title-md">
        Reconcile duplicate recorded costs
      </Typography>
      <Typography level="body-sm">
        Keep the authoritative bill or cost. Exclude only a duplicate of the
        same expense; a credit is already subtracted automatically.
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <Select
          aria-label="Duplicate cost to exclude"
          placeholder="Duplicate cost to exclude"
          value={duplicate || null}
          onChange={(_, v) => setDuplicate(v || "")}
        >
          {data.sources
            .filter((s) => Number(s.amount) >= 0)
            .map((s) => (
              <Option key={s.ref} value={s.ref}>
                {s.label} · {usd(s.amount)}
              </Option>
            ))}
        </Select>
        <Select
          aria-label="Authoritative cost to retain"
          placeholder="Authoritative cost to retain"
          value={canonical || null}
          onChange={(_, v) => setCanonical(v || "")}
        >
          {data.sources
            .filter((s) => Number(s.amount) >= 0)
            .map((s) => (
              <Option key={s.ref} value={s.ref}>
                {s.label} · {usd(s.amount)}
              </Option>
            ))}
        </Select>
      </Stack>
      <Input
        placeholder="Why are these the same expense?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        slotProps={{ input: { "aria-label": "Reconciliation reason" } }}
      />
      <Button
        variant="soft"
        disabled={
          !duplicate || !canonical || duplicate === canonical || !reason.trim()
        }
        onClick={() => {
          setDraft({
            ...draft,
            reconciliations: [
              ...draft.reconciliations,
              { duplicate, canonical, reason },
            ],
          });
          setDuplicate("");
          setCanonical("");
          setReason("");
        }}
      >
        Add duplicate reconciliation
      </Button>
      {draft.reconciliations.map((r, i) => (
        <Alert
          key={i}
          endDecorator={
            <Button
              variant="plain"
              onClick={() =>
                setDraft({
                  ...draft,
                  reconciliations: draft.reconciliations.filter(
                    (_, index) => index !== i,
                  ),
                })
              }
            >
              Remove
            </Button>
          }
        >
          Exclude{" "}
          {data.sources.find((s) => s.ref === r.duplicate)?.label ||
            r.duplicate}
          ; retain{" "}
          {data.sources.find((s) => s.ref === r.canonical)?.label ||
            r.canonical}
          . {r.reason}
        </Alert>
      ))}
      <Divider />
      <Typography level="title-md">
        Match commitments to recorded costs
      </Typography>
      <Typography level="body-sm">
        A $5,000 order with $2,000 already recorded has $3,000 left committed.
        Delivery completion alone does not remove that commitment. Approved
        subcontractor invoices automatically match their work order.
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <Select
          aria-label="Material order"
          placeholder="Material / work order"
          value={order || null}
          onChange={(_, v) => setOrder(v || "")}
        >
          {data.orders.map((o) => (
            <Option key={o.ref} value={o.ref}>
              {o.label} · {usd(o.amount)}
            </Option>
          ))}
        </Select>
        <Select
          aria-label="Recorded order cost"
          placeholder="Recorded order cost"
          value={source || null}
          onChange={(_, v) => setSource(v || "")}
        >
          {data.sources
            .filter(
              (s) =>
                Number(s.amount) > 0 &&
                !s.order_ref &&
                !draft.reconciliations.some((r) => r.duplicate === s.ref),
            )
            .map((s) => (
              <Option key={s.ref} value={s.ref}>
                {s.label}
              </Option>
            ))}
        </Select>
        <Field
          label="Amount already recorded"
          value={allocationAmount}
          onChange={setAllocationAmount}
        />
      </Stack>
      <Button
        variant="soft"
        disabled={!order || !source || !(Number(allocationAmount) > 0)}
        onClick={() => {
          setDraft({
            ...draft,
            allocations: [
              ...draft.allocations,
              { order, source, amount: allocationAmount },
            ],
          });
          setOrder("");
          setSource("");
          setAllocationAmount("");
        }}
      >
        Add cost match
      </Button>
      {draft.allocations.map((a, i) => (
        <Alert
          key={i}
          endDecorator={
            <Button
              variant="plain"
              onClick={() =>
                setDraft({
                  ...draft,
                  allocations: draft.allocations.filter(
                    (_, index) => index !== i,
                  ),
                })
              }
            >
              Remove
            </Button>
          }
        >
          {data.orders.find((o) => o.ref === a.order)?.label || a.order}:{" "}
          {usd(a.amount)} matched to{" "}
          {data.sources.find((s) => s.ref === a.source)?.label || a.source}
        </Alert>
      ))}
      <FormControl>
        <FormLabel>Approval note (optional)</FormLabel>
        <Textarea
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          minRows={2}
        />
      </FormControl>
      {save.error && (
        <Alert color="danger">
          {errorText(save.error)} Reload the page if another person approved a
          newer revision.
        </Alert>
      )}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, flexShrink: 0 }}>
      <Button variant="plain" color="neutral" disabled={save.isPending} onClick={onClose}>Cancel</Button>
      <Button
        type="submit"
        loading={save.isPending}
      >
        Approve cost plan
      </Button>
      </Stack>
    </Box>
      </ModalDialog>
    </Modal>
  );
}

export function ProfitDeskPortfolio() {
  const query = useQuery({
    queryKey: ["profit-desk"],
    queryFn: async () =>
      (await apiClient.get<ProfitReport>("/api/analytics/profit-desk/")).data,
  });
  const [attentionOnly, setAttentionOnly] = useState(false);
  if (query.error)
    return <Alert color="danger">{errorText(query.error)}</Alert>;
  if (!query.data) return <Typography>Loading profit desk…</Typography>;
  const data = query.data;
  return (
    <Stack spacing={2}>
      <Typography level="h3">Job Profit Desk</Typography>
      <Typography>
        All accessible jobs, using lifetime contract values and costs. Select a
        job to approve its budget, reconcile costs and review the evidence.
      </Typography>
      <Warnings warnings={data.warnings} />
      <Checkbox
        label="Show jobs needing attention"
        checked={attentionOnly}
        onChange={(e) => setAttentionOnly(e.target.checked)}
      />
      <ResponsiveTable
        rows={data.jobs.filter(
          (r) => !attentionOnly || r.below_floor || r.warnings.length,
        )}
        getRowKey={(r) => r.job_id}
        pagination={{ pageSize: 20, itemNoun: "jobs" }}
        columns={[
          {
            key: "job",
            header: "Job",
            mobileVariant: "header",
            render: (r) => (
              <Link component={RouterLink} to={r.url}>
                {r.name}
              </Link>
            ),
            sortValue: (r) => r.name,
          },
          {
            key: "value",
            header: "Contract",
            render: (r) => usd(r.contract_value),
            sortValue: (r) => Number(r.contract_value),
          },
          {
            key: "cost",
            header: "Projected final cost",
            render: (r) => usd(r.projected_costs),
          },
          {
            key: "margin",
            header: "Projected gross margin",
            render: (r) => (
              <Chip color={r.below_floor ? "danger" : "neutral"}>
                {r.projected_margin === null
                  ? "No contract value"
                  : `${r.projected_margin}%`}
              </Chip>
            ),
            sortValue: (r) => Number(r.projected_margin),
          },
          {
            key: "ar",
            header: "Issued AR",
            render: (r) => usd(r.issued_receivables),
          },
          {
            key: "coverage",
            header: "Review",
            mobileVariant: "body",
            render: (r) =>
              r.warnings.length
                ? `${r.warnings.length} coverage / reconciliation notices`
                : `Reviewed · budget v${r.plan.revision}`,
          },
        ]}
      />
    </Stack>
  );
}

export function CashDesk() {
  const { can } = usePermission();
  const query = useQuery({
    queryKey: ["cash-desk"],
    queryFn: async () =>
      (await apiClient.get<CashForecast>("/api/analytics/cash-plan/")).data,
  });
  const [editing, setEditing] = useState(false);
  if (query.error)
    return <Alert color="danger">{errorText(query.error)}</Alert>;
  if (!query.data) return <Typography>Loading cash forecast…</Typography>;
  const data = query.data;
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        sx={{ flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}
      >
        <Typography level="h3">Cash Forecast</Typography>
        <Button
          disabled={!can("billing", "edit")}
          onClick={() => setEditing(!editing)}
        >
          {editing ? "Close assumptions" : "Review cash assumptions"}
        </Button>
      </Stack>
      <Typography>
        Starting {data.as_of} with {usd(data.opening_cash)}. Compare expected
        collections with a {data.delay_days}-day delay. This is a forecast of
        recorded obligations and reviewed assumptions.
      </Typography>
      <Warnings warnings={data.warnings} />
      {editing && (
        <CashPlanEditor data={data} onSaved={() => setEditing(false)} />
      )}
      <ResponsiveTable
        rows={data.weeks}
        getRowKey={(r) => r.week}
        columns={[
          {
            key: "week",
            header: "Week starting",
            mobileVariant: "header",
            render: (r) => r.week,
          },
          {
            key: "receipts",
            header: "Expected receipts",
            render: (r) => usd(r.receipts),
          },
          {
            key: "payments",
            header: "Expected payments",
            render: (r) => usd(r.payments),
          },
          {
            key: "cash",
            header: "Closing cash",
            render: (r) => (
              <Typography
                color={Number(r.closing_cash) < 0 ? "danger" : "neutral"}
              >
                {usd(r.closing_cash)}
              </Typography>
            ),
          },
          {
            key: "delayed",
            header: `With ${data.delay_days}-day collection delay`,
            mobileLabel: "Delayed cash",
            render: (r) => (
              <Typography
                color={
                  Number(r.delayed_closing_cash) < 0 ? "danger" : "neutral"
                }
              >
                {usd(r.delayed_closing_cash)}
              </Typography>
            ),
          },
        ]}
      />
      <Typography level="title-lg">Forecast source records</Typography>
      <Typography level="body-sm">
        Sources beyond the selected forecast horizon remain listed below.
      </Typography>
      <ResponsiveTable
        rows={data.events}
        getRowKey={(r) => r.ref}
        pagination={{ pageSize: 15 }}
        columns={[
          {
            key: "source",
            header: "Source",
            mobileVariant: "header",
            render: (r) =>
              r.url ? (
                <Link component={RouterLink} to={r.url}>
                  {r.label}
                </Link>
              ) : (
                r.label
              ),
          },
          { key: "date", header: "Expected date", render: (r) => r.date },
          {
            key: "amount",
            header: "Amount",
            render: (r) =>
              `${r.direction === "in" ? "+" : "−"}${usd(r.amount)}`,
          },
          {
            key: "assumption",
            header: "Basis",
            mobileVariant: "body",
            render: (r) =>
              r.excluded
                ? "Excluded: reviewed duplicate payable"
                : r.assumption || "Approved unpaid obligation",
          },
        ]}
      />
      <History plan={data.plan} />
    </Stack>
  );
}

function CashPlanEditor({
  data,
  onSaved,
}: {
  data: CashForecast;
  onSaved: () => void;
}) {
  const client = useQueryClient();
  const [expectedRevision] = useState(data.plan.revision);
  const [draft, setDraft] = useState<CashPlan>({
    opening_cash: data.opening_cash,
    as_of: today(),
    weeks: 13,
    delay_days: 14,
    expenses: [],
    collections: [],
    reconciliations: [],
    ...data.plan.data,
    note: "",
  });
  const [duplicate, setDuplicate] = useState("");
  const [canonical, setCanonical] = useState("");
  const [reason, setReason] = useState("");
  const payables = data.events.filter(
    (e) => e.ref.startsWith("vendor-bill:") || e.ref.startsWith("sub-invoice:"),
  );
  const save = useMutation({
    mutationFn: async () =>
      (
        await apiClient.patch<CashForecast>("/api/analytics/cash-plan/", {
          ...draft,
          expected_revision: expectedRevision,
        })
      ).data,
    onSuccess: (result) => {
      client.setQueryData(["cash-desk"], result);
      onSaved();
    },
  });
  return (
    <Card
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Typography level="title-lg">Cash assumptions</Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ flexWrap: "wrap" }}
      >
        <Field
          label="Opening cash ($)"
          value={draft.opening_cash}
          onChange={(v) => setDraft({ ...draft, opening_cash: v })}
        />
        <Field
          label="As of date"
          type="date"
          value={draft.as_of}
          onChange={(v) => setDraft({ ...draft, as_of: v })}
        />
        <Field
          label="Forecast weeks"
          value={draft.weeks}
          onChange={(v) => setDraft({ ...draft, weeks: Number(v) })}
        />
        <Field
          label="Collection delay (days)"
          value={draft.delay_days}
          onChange={(v) => setDraft({ ...draft, delay_days: Number(v) })}
        />
      </Stack>
      <Typography level="title-md">Expected collection dates</Typography>
      {data.events
        .filter((e) => e.ref.startsWith("invoice:"))
        .map((event) => {
          const id = Number(event.ref.split(":")[1]);
          const chosen = draft.collections.find((c) => c.invoice_id === id);
          return (
            <Field
              key={event.ref}
              label={`${event.label} expected collection`}
              type="date"
              value={chosen?.date || event.date}
              onChange={(v) =>
                setDraft({
                  ...draft,
                  collections: [
                    ...draft.collections.filter((c) => c.invoice_id !== id),
                    ...(v ? [{ invoice_id: id, date: v }] : []),
                  ],
                })
              }
            />
          );
        })}
      <Typography level="title-md">Duplicate payables</Typography>
      <Typography level="body-sm">
        When the same obligation was entered as both a vendor bill and a
        subcontractor invoice, keep one in the forecast. This does not alter the
        accounting records.
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
        <Select
          aria-label="Duplicate payable"
          placeholder="Duplicate payable"
          value={duplicate || null}
          onChange={(_, v) => setDuplicate(v || "")}
        >
          {payables.map((e) => (
            <Option key={e.ref} value={e.ref}>
              {e.label} · {usd(e.amount)}
            </Option>
          ))}
        </Select>
        <Select
          aria-label="Payable to retain"
          placeholder="Payable to retain"
          value={canonical || null}
          onChange={(_, v) => setCanonical(v || "")}
        >
          {payables.map((e) => (
            <Option key={e.ref} value={e.ref}>
              {e.label} · {usd(e.amount)}
            </Option>
          ))}
        </Select>
      </Stack>
      <Input
        placeholder="Why are these the same obligation?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        slotProps={{ input: { "aria-label": "Payable reconciliation reason" } }}
      />
      <Button
        variant="soft"
        disabled={
          !duplicate || !canonical || duplicate === canonical || !reason.trim()
        }
        onClick={() => {
          setDraft({
            ...draft,
            reconciliations: [
              ...draft.reconciliations,
              { duplicate, canonical, reason },
            ],
          });
          setDuplicate("");
          setCanonical("");
          setReason("");
        }}
      >
        Add payable reconciliation
      </Button>
      {draft.reconciliations.map((r, i) => (
        <Alert
          key={i}
          endDecorator={
            <Button
              variant="plain"
              onClick={() =>
                setDraft({
                  ...draft,
                  reconciliations: draft.reconciliations.filter(
                    (_, index) => index !== i,
                  ),
                })
              }
            >
              Remove
            </Button>
          }
        >
          Exclude{" "}
          {payables.find((e) => e.ref === r.duplicate)?.label || r.duplicate};
          retain{" "}
          {payables.find((e) => e.ref === r.canonical)?.label || r.canonical}.{" "}
          {r.reason}
        </Alert>
      ))}
      <Typography level="title-md">
        Operating expenses not already in bills
      </Typography>
      <Typography level="body-sm">
        Add payroll, rent or other expected costs once. Remove a manual expense
        when its payable is recorded. A recurrence of 0 weeks creates one
        occurrence.
      </Typography>
      {draft.expenses.map((expense, i) => (
        <Card key={i} variant="soft">
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            {(
              ["label", "amount", "date", "every_weeks", "occurrences"] as const
            ).map((key) => (
              <Field
                key={key}
                label={`${key.replaceAll("_", " ")} (expense ${i + 1})`}
                value={expense[key]}
                type={
                  key === "label" ? "text" : key === "date" ? "date" : "number"
                }
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    expenses: draft.expenses.map((e, index) =>
                      index === i
                        ? {
                            ...e,
                            [key]:
                              key === "every_weeks" || key === "occurrences"
                                ? Number(v)
                                : v,
                          }
                        : e,
                    ),
                  })
                }
              />
            ))}
            <Button
              variant="plain"
              onClick={() =>
                setDraft({
                  ...draft,
                  expenses: draft.expenses.filter((_, index) => index !== i),
                })
              }
            >
              Remove expense
            </Button>
          </Stack>
        </Card>
      ))}
      <Button
        variant="soft"
        onClick={() =>
          setDraft({
            ...draft,
            expenses: [
              ...draft.expenses,
              {
                label: "",
                amount: "0",
                date: draft.as_of,
                every_weeks: 0,
                occurrences: 1,
              },
            ],
          })
        }
      >
        Add operating expense
      </Button>
      <FormControl>
        <FormLabel>Approval note</FormLabel>
        <Textarea
          required
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
        />
      </FormControl>
      {save.error && <Alert color="danger">{errorText(save.error)}</Alert>}
      <Button
        type="submit"
        disabled={!draft.note.trim()}
        loading={save.isPending}
      >
        Approve cash assumptions
      </Button>
    </Card>
  );
}
